/* eslint-disable react-hooks/exhaustive-deps */
import { produce } from 'immer';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import useMount from '../../../../../common/hooks/useMount';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import { TaxPayer } from '../../../constants';
import { BuyerTypes, FilterTypes } from '../../../enums';
import { PriceRange } from '../../../models/PriceRange';
import { resources } from '../../../resources/resources';
import { discoverService } from '../../../services/discover';
import { filterService } from '../../../services/filters';
import applicationCrumbs from '../../ApplicationCrumbs';
import ProductListPanel from '../../CategoryPage/Components/ProductsListPanel';
import {
  AvailableInFilterState,
  BrandFilterState,
  BuyerTypeFilterState,
  FilterSelectionsState,
  PriceRangeFilterState,
  ProductListState,
  ProductsOnOfferFilterState,
  TaxationLevelFilterState,
  filterSelectionDefaults,
} from '../../CategoryPage/types';
import {
  getAvailableInList,
  getBrandList,
  getDiscountedProductsCount,
  getFiltredProducts,
  getPriceRange,
  getSortedProduts,
  getTaxList,
  getUpdatedFilters,
  hasSelectedFilters,
} from '../../CategoryPage/utils';
import FilterPanel from '../../Common/Filters';
import MobileProductsComponent from '../../ProductsPage/ProductsSearched/MobileSearch/MobileSearchResults';
import DiscoverCategoryFilter from '../components/DiscoverCategoryFilter';

export default function DiscoverProductsPage() {
  const { discoverCategoryId } = useParams();
  const { location } = useParams();
  const { t: translate } = useTranslation();
  const { isTablet } = useBreakpoints();

  const navigate = useNavigate();
  const buyerTypeList = useMemo(
    () => [
      {
        buyerType: BuyerTypes.Employee,
        name: translate(resources.filters.buyerType.employee),
      },
      {
        buyerType: BuyerTypes.Dependent,
        name: translate(resources.filters.buyerType.dependent),
      },
    ],
    [translate],
  );

  // #region state
  const [pageCategory, setPageCategory] = useState<string>();
  const [products, setProducts] = useState<ProductListState>({
    productList: undefined,
    filtredProductList: undefined,
  });
  const [productsOnOfferFilterState, setProductsOnOfferFilterState] =
    useState<ProductsOnOfferFilterState>({
      discountedProductsCount: 0,
    });

  const [priceRangeFilterState, setPriceRangeFilterState] =
    useState<PriceRangeFilterState>({
      priceRange: undefined,
    });

  const [brandFilterState, setBrandFilterState] = useState<BrandFilterState>({
    brandList: undefined,
    filteredBrandList: undefined,
  });
  const [taxationLevelFilterState, setTaxationLevelFilterState] =
    useState<TaxationLevelFilterState>({
      taxationLevelList: undefined,
      filteredTaxationLevelList: undefined,
    });
  const [
    employerTaxationLevelFilterState,
    setEmployerTaxationLevelFilterState,
  ] = useState<TaxationLevelFilterState>({
    taxationLevelList: undefined,
    filteredTaxationLevelList: undefined,
  });
  const [availableInFilterState, setAvailableInFilterState] =
    useState<AvailableInFilterState>({
      availableInList: undefined,
      filteredAvailableInList: undefined,
    });
  const [buyerTypeFilterState, setBuyerTypeFilterState] =
    useState<BuyerTypeFilterState>({
      buyerTypeList,
      filteredBuyerTypeList: buyerTypeList,
    });

  const [filterSelectionState, setFilterSelectionState] =
    useState<FilterSelectionsState>(filterSelectionDefaults);
  const [currentFilterType, setCurrentFilterType] = useState<number>();
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(
    new Set<number>(),
  );
  // #endregion

  const crumbs = applicationCrumbs(translate, pageCategory ?? location);

  const hasFilters =
    products.productList?.length !== products.filtredProductList?.length;

  const categoryIds = useMemo(
    () => _.uniq(products.productList?.map(p => p.categoryId)),
    [products.productList],
  );

  const pageTitle = discoverCategoryId
    ? pageCategory
    : `${translate(resources.discover.searchByLocation.title)} ${pageCategory}`;

  // #region load data
  const loadPageCategory = async (abortController?: AbortController) => {
    if (discoverCategoryId) {
      const resultTitle = await discoverService.getTitleById(
        Number(discoverCategoryId),
        abortController,
      );

      if (!resultTitle.hasErrors) {
        setPageCategory(resultTitle.data);
      }
    }

    if (location) {
      setPageCategory(location);
    }
  };

  const loadProducts = async (abortController?: AbortController) => {
    if (discoverCategoryId) {
      const result = await discoverService.getProductsForCategory(
        Number(discoverCategoryId),
        abortController,
      );

      if (!result.hasErrors) {
        if (result.data.length > 0) {
          setProducts({
            productList: result.data,
            filtredProductList: result.data,
          });
          setProductsOnOfferFilterState({
            discountedProductsCount: getDiscountedProductsCount(result.data),
          });
          setPriceRangeFilterState({ priceRange: getPriceRange(result.data) });
        } else {
          navigate('/not-found');
        }
      }
    }

    if (location) {
      const result = await discoverService.getProductsIn(
        location,
        abortController,
      );

      if (!result.hasErrors) {
        if (result.data.length > 0) {
          setProducts({
            productList: result.data,
            filtredProductList: result.data,
          });
          setProductsOnOfferFilterState({
            discountedProductsCount: getDiscountedProductsCount(result.data),
          });
          setPriceRangeFilterState({ priceRange: getPriceRange(result.data) });
        } else {
          navigate('/not-found');
        }
      }
    }
  };

  const loadBrandList = async () => {
    const result =
      await filterService.getDiscoverBrandFilterForCategories(categoryIds);

    if (!result.hasErrors) {
      setBrandFilterState({
        brandList: result.data,
        filteredBrandList: getBrandList(
          products.filtredProductList ?? [],
          result.data,
        ),
      });
    }
  };

  const loadTaxationLevelList = async () => {
    const result =
      await filterService.getDiscoverTaxFilterForCategories(categoryIds);

    if (!result.hasErrors) {
      const employeeTaxList = result.data.filter(
        it => it.payer == TaxPayer.Employee,
      );
      const employerTaxList = result.data.filter(
        it => it.payer == TaxPayer.Employee,
      );
      setTaxationLevelFilterState({
        taxationLevelList: employeeTaxList,
        filteredTaxationLevelList: getTaxList(
          products.filtredProductList ?? [],
          employeeTaxList,
        ),
      });
      setEmployerTaxationLevelFilterState({
        taxationLevelList: employerTaxList,
        filteredTaxationLevelList: getTaxList(
          products.filtredProductList ?? [],
          employerTaxList,
        ),
      });
    }
  };

  const loadAvailableInList = async () => {
    setAvailableInFilterState({
      availableInList: undefined,
      filteredAvailableInList: undefined,
    });
    const result =
      await filterService.getDiscoverCountyFilterForCategories(categoryIds);

    if (!result.hasErrors) {
      setAvailableInFilterState({
        availableInList: result.data,
        filteredAvailableInList: getAvailableInList(
          products.filtredProductList ?? [],
          result.data,
        ),
      });
    }
  };
  // #endregion

  // #region filter handlers
  const handleSortingOrderChanged = (id: number) => {
    setProducts(
      produce(draft => {
        draft.filtredProductList = getSortedProduts[id](
          draft.filtredProductList,
        );
      }),
    );
  };

  const handlePriceRangeFilterChanged = useCallback(
    (range: PriceRange) => {
      setFilterSelectionState(
        produce(draft => {
          draft.priceRange = range;
        }),
      );
      setCurrentFilterType(FilterTypes.PriceRange);
    },
    [filterSelectionState.priceRange],
  );

  const handleAvailableInFilterChanged = useCallback((countyIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedCountyIds = countyIds;
      }),
    );
    setCurrentFilterType(FilterTypes.AvailableIn);
  }, []);

  const handleBrandFilterChanged = useCallback((brandIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedBrandIds = brandIds;
      }),
    );
    setCurrentFilterType(FilterTypes.Brand);
  }, []);

  const handleTaxFilterChanged = useCallback((taxClassIds: number[]) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedTaxClassIds = taxClassIds;
      }),
    );
    setCurrentFilterType(FilterTypes.TaxationLevel);
  }, []);

  const handleEmployerTaxFilterChanged = useCallback(
    (taxClassIds: number[]) => {
      setFilterSelectionState(
        produce(draft => {
          draft.selectedEmployerTaxClassIds = taxClassIds;
        }),
      );
      setCurrentFilterType(FilterTypes.EmployerTaxationLevel);
    },
    [],
  );

  const handleProductsOnOfferFilterChanged = useCallback(
    (isSelected: boolean) => {
      setFilterSelectionState(
        produce(draft => {
          draft.showDiscountedProducts = isSelected;
        }),
      );
      setCurrentFilterType(FilterTypes.ShowDiscountedProducts);
    },
    [],
  );

  const handleBuyerTypeFilterChanged = useCallback(
    (buyerTypes: BuyerTypes[]) => {
      setFilterSelectionState(
        produce(draft => {
          draft.selectedBuyerTypes = buyerTypes;
        }),
      );
      setCurrentFilterType(FilterTypes.BuyerType);
    },
    [],
  );

  const handleClearFilters = () => {
    setFilterSelectionState(filterSelectionDefaults);
    setSelectedCategories(new Set<number>());

    if (products.productList) {
      setPriceRangeFilterState({
        priceRange: getPriceRange(products.productList),
      });
    }

    setProducts(
      produce(d => {
        d.filtredProductList = d.productList;
      }),
    );
  };

  const handleCategoryChanged = useCallback((categoriesIds: Set<number>) => {
    setFilterSelectionState(
      produce(draft => {
        draft.selectedCategories = categoriesIds;
      }),
    );
    setCurrentFilterType(FilterTypes.Category);
  }, []);
  // #endregion

  // #region effects
  useMount(abortController => {
    loadPageCategory(abortController);
    loadProducts(abortController);
  });

  useEffect(() => {
    if (categoryIds.length > 0) {
      loadAvailableInList();
      loadBrandList();
      loadTaxationLevelList();
    }
  }, [categoryIds]);

  useEffect(() => {
    setProducts(
      produce(d => {
        d.filtredProductList = getFiltredProducts(
          products.productList,
          filterSelectionState,
        );
      }),
    );
  }, [
    filterSelectionState.selectedBrandIds,
    filterSelectionState.selectedBuyerTypes,
    filterSelectionState.selectedCountyIds,
    filterSelectionState.selectedTaxClassIds,
    filterSelectionState.showDiscountedProducts,
    filterSelectionState.selectedCategories,
    filterSelectionState.priceRange,
    products.productList,
  ]);

  useEffect(() => {
    const filters = getUpdatedFilters(
      products.productList,
      products.filtredProductList,
      availableInFilterState.availableInList,
      brandFilterState.brandList,
      taxationLevelFilterState.taxationLevelList,
      employerTaxationLevelFilterState.taxationLevelList,
      buyerTypeFilterState.buyerTypeList,
      selectedCategories,
      currentFilterType === FilterTypes.PriceRange,
      hasSelectedFilters(filterSelectionState),
    );
    if (filters) {
      if (currentFilterType !== FilterTypes.AvailableIn)
        setAvailableInFilterState(
          produce(d => {
            d.filteredAvailableInList = filters.filteredCounties;
          }),
        );

      if (currentFilterType !== FilterTypes.TaxationLevel)
        setTaxationLevelFilterState(
          produce(d => {
            d.filteredTaxationLevelList = filters.filteredTax;
          }),
        );

      if (currentFilterType !== FilterTypes.Brand)
        setBrandFilterState(
          produce(d => {
            d.filteredBrandList = filters.filteredBrands;
          }),
        );

      if (currentFilterType !== FilterTypes.BuyerType)
        setBuyerTypeFilterState(
          produce(d => {
            d.filteredBuyerTypeList = filters.filteredBuyerTypes;
          }),
        );
      if (currentFilterType !== FilterTypes.Category) {
        setSelectedCategories(filters.filteredSelectedCategories);
      }
      if (currentFilterType !== FilterTypes.PriceRange)
        setPriceRangeFilterState({ priceRange: filters.priceRange });

      if (currentFilterType !== FilterTypes.ShowDiscountedProducts)
        setProductsOnOfferFilterState({
          discountedProductsCount: filters.discountedProductsCount,
        });
    }
  }, [products.filtredProductList]);

  useEffect(() => {
    if (!products.filtredProductList && products.productList) {
      setProducts(
        produce(d => {
          d.filtredProductList = d.productList;
        }),
      );
    }
  }, [products.filtredProductList]);

  useEffect(() => {
    setFilterSelectionState(filterSelectionDefaults);
  }, [products.productList]);
  // #endregion

  // #region tsx
  return (
    <>
      <Breadcrumbs
        entries={[
          crumbs.home,
          crumbs.discover,
          discoverCategoryId ? crumbs.discoverParam : crumbs.search,
        ]}
      />
      {!isTablet ? (
        <section className="section-main section-actions search-results-page">
          <div className="container">
            <div className="area-actions">
              <DiscoverCategoryFilter
                products={products.filtredProductList}
                selectedCategoryIds={selectedCategories}
                handleChange={handleCategoryChanged}
              />
              <FilterPanel
                availableInList={availableInFilterState.filteredAvailableInList}
                brandList={brandFilterState.filteredBrandList}
                buyerTypeList={buyerTypeFilterState.filteredBuyerTypeList}
                taxationLevelList={
                  taxationLevelFilterState.filteredTaxationLevelList
                }
                employerTaxationLevelList={
                  employerTaxationLevelFilterState.filteredTaxationLevelList
                }
                priceRange={priceRangeFilterState.priceRange}
                discountedProdutsCount={
                  productsOnOfferFilterState.discountedProductsCount
                }
                prevSelectionState={filterSelectionState}
                hasActiveFilters={hasFilters}
                handlePriceRangeFilterChanged={handlePriceRangeFilterChanged}
                handleAvailableInFilterChanged={handleAvailableInFilterChanged}
                handleBrandFilterChanged={handleBrandFilterChanged}
                handleBuyerTypeFilterChanged={handleBuyerTypeFilterChanged}
                handleTaxFilterChanged={handleTaxFilterChanged}
                handleEmployerTaxFilterChanged={handleEmployerTaxFilterChanged}
                handleProductsOnOfferFilterChanged={
                  handleProductsOnOfferFilterChanged
                }
                handleClearFilters={handleClearFilters}
                filteredProducts={products.filtredProductList}
              />
            </div>
            <div className="area-content">
              <h1>{pageTitle}</h1>
              <ProductListPanel
                productList={products.filtredProductList}
                isDiscountVoucherList={false}
                isBenefitBucksList={false}
                handleSortingOrderChanged={handleSortingOrderChanged}
              />
            </div>
          </div>
        </section>
      ) : (
        <MobileProductsComponent
          filteredProducts={products.filtredProductList}
          handleSortingOrderChange={handleSortingOrderChanged}
          filters={{
            filteredAvailableInList:
              availableInFilterState.filteredAvailableInList,
            filteredBrandList: brandFilterState.filteredBrandList,
            filteredTaxationLevelList:
              taxationLevelFilterState.filteredTaxationLevelList,
            filteredEmployerTaxationLevelList:
              employerTaxationLevelFilterState.filteredTaxationLevelList,
            priceRange: priceRangeFilterState.priceRange,
            discountedProductsCount:
              productsOnOfferFilterState.discountedProductsCount,
            selectionFilterState: filterSelectionState,
            hasActiveFilters: hasFilters,
            priceRangeFilterChanged: handlePriceRangeFilterChanged,
            availableInFilterChanged: handleAvailableInFilterChanged,
            brandFilterChanged: handleBrandFilterChanged,
            buyerTypeFilterChanged: handleBuyerTypeFilterChanged,
            taxFilterChanged: handleTaxFilterChanged,
            employerTaxFilterChanged: handleEmployerTaxFilterChanged,
            productsOnOfferFilterChanged: handleProductsOnOfferFilterChanged,
            clearFilters: handleClearFilters,
          }}
          isTabletView={isTablet}
          isDiscountVoucherList={false}
          isBenefitBucksList={false}
          title={pageTitle}
        />
      )}
    </>
  );
  // #endregion
}
